import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Space, Typography, Alert, Radio } from 'antd';
import useTranslation from '@hooks/useTranslation';
import { goToCheckoutStepAction, selectCheckoutIsProcessing, toggleIsProcessingAction } from '@src/app/reducers/checkout/checkoutSlice';
import { AddressElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import checkoutApi, { PriceInfo, ValidStripeAddress } from '@src/api/checkout/checkoutApi';
import type { StripeAddressElementChangeEvent, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { selectSignedUserProfile, updateOrganizationAction } from '@src/app/reducers/user/userSlice';
import StyledButton from '@components/form-control/styledButton/styledButton';
import styles from './StripeCheckoutForm.module.css';
import { SubscriptionInfo } from '@kemu-io/kemu-types';
import PaymentMethodCard from './PaymentMethodCard/PaymentMethodCard';

type Props = {
  priceInfo: PriceInfo;
  currentPlanInfo: SubscriptionInfo;
  paymentMethods: {
    id: string;
    address: ValidStripeAddress;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
    isDefaultMethod?: boolean;
  }[];
  onClose?: () => void;
  onDeletePaymentMethod?: (paymentMethodId: string) => Promise<void>;
};

/**
 * Stripe checkout form that handles both existing and new payment methods
 * @param props - Component props containing price info and payment methods
 * @returns React.JSX.Element
 */
const StripeCheckoutForm = (props: Props) => {
  const { priceInfo, paymentMethods, onClose, onDeletePaymentMethod } = props;
  const t = useTranslation('Checkout.Payment');
  const dispatch = useDispatch();
  const user = useSelector(selectSignedUserProfile);
  const stripe = useStripe();
  const elements = useElements();
  // const [isLoading, setIsLoading] = useState(false);
  const isLoading = useSelector(selectCheckoutIsProcessing);
  const [deletingPaymentMethodId, setDeletingPaymentMethodId] = useState<string | null>(null);
  // const [billingAddress, setBillingAddress] = useState<StripeAddressElementChangeEvent['value']['address'] | null>(null);
  // const [taxesCalculation, setTaxesCalculation] = useState<CalculateTaxesResponse | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | 'new'>('');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isAddressComplete, setIsAddressComplete] = useState(false);
  const [isPaymentElementComplete, setIsPaymentElementComplete] = useState(false);

  const toggleIsProcessing = useCallback((isProcessing: boolean) => {
    dispatch(toggleIsProcessingAction(isProcessing));
  }, [dispatch]);

  // const handleCalculateTaxes = useDebouncedCallback(async () => {
  //   // NOTE: disabled tax calculation for now since price is supposed to be GST inclusive
  //   /* if (!billingAddress?.country) { return; }
    
  //   // Cancel the previous calculation
  //   handleCalculateTaxes?.cancel();
  //   const amount = priceInfo.unitAmount;
  //   const result = await checkoutApi.calculateTaxes(amount, billingAddress, priceInfo.currency);
  //   setTaxesCalculation(result); */
  // }, 300);

  const handleAddressChange = (event: StripeAddressElementChangeEvent) => {
    setIsAddressComplete(event.complete);
    // if (event.complete) {
    //   setBillingAddress(event.value.address);
    //   handleCalculateTaxes();
    // }
  };


  const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
    setIsPaymentElementComplete(event.complete);
  };

  /**
   * Handles deletion of a payment method with confirmation
   * @param paymentMethodId - The ID of the payment method to delete
   */
  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    try {
      if (!onDeletePaymentMethod) { return; }
      setDeletingPaymentMethodId(paymentMethodId);
      await onDeletePaymentMethod(paymentMethodId);
      // If the deleted payment method was selected, switch to 'new'
      if (selectedPaymentMethod === paymentMethodId) {
        setSelectedPaymentMethod('new');
      }
    } catch (error) {
      console.error('Failed to delete payment method:', error);
      setCheckoutError(`Failed to delete payment method: ${error.message}`);
    }finally {
      setDeletingPaymentMethodId(null);
    }
  };

  // Determine if the form is valid and ready to submit
  const isFormValid = useMemo(() => {
    if (!stripe || !elements) { return false; }
    
    // Prevent submission if any payment method is being deleted
    if (deletingPaymentMethodId) { return false; }
    
    // If using an existing payment method, no additional validation needed
    if (selectedPaymentMethod !== 'new') {
      return true;
    }
    
    // For new payment methods, require both address and payment element to be complete
    return isAddressComplete && isPaymentElementComplete;
  }, [stripe, elements, selectedPaymentMethod, isAddressComplete, isPaymentElementComplete, deletingPaymentMethodId]);

  const handleSubscribeToNewTier = async (priceId: string, paymentMethodId: string) => {
    try {
      toggleIsProcessing(true);
      const result = await checkoutApi.subscribeToTier({ priceId, paymentMethodId });
      console.log('subscribeToTier result', result);

      if (!result.organization) {
        console.error('Invalid server response: ', result);
        setCheckoutError('Invalid server response');
        return;
      }

      // Update user subscription in Redux state
      dispatch(updateOrganizationAction(result.organization));
      dispatch(goToCheckoutStepAction('success'));
      onClose?.();
    } catch (e) {
      console.error(`Failed to update subscription: ${e.message}`);
      setCheckoutError(`Failed to update subscription: ${e.message}`);
    } finally {
      toggleIsProcessing(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (selectedPaymentMethod !== 'new') {
      return handleSubscribeToNewTier(priceInfo.id, selectedPaymentMethod);
    }

    toggleIsProcessing(true);

    // Use confirmSetup for new payment methods (SetupIntent)
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}`,
      },
      redirect: 'if_required',
    });

    if (error) {
      console.error(error.message);
      setCheckoutError(error.message || 'Failed to confirm card setup');
      toggleIsProcessing(false);
    } else if (setupIntent && setupIntent.status === 'succeeded') {
      await handleSubscribeToNewTier(priceInfo.id, setupIntent.payment_method as string);
      toggleIsProcessing(false);
    } else if (setupIntent && setupIntent.status === 'requires_action') {
      const { error: actionError } = await stripe.confirmCardSetup(setupIntent.client_secret as string);
      if (actionError) {
        console.error(actionError.message);
        setCheckoutError(actionError.message || 'Failed to confirm card setup');
      } else {
        await handleSubscribeToNewTier(priceInfo.id, setupIntent.payment_method as string);
      }
      toggleIsProcessing(false);
    }
  };

  useEffect(() => {
    if (isLoading) {
      setCheckoutError(null);
    }
  }, [isLoading]);

  useEffect(() => {
    // Set default payment method as selected when available
    if (paymentMethods.length > 0) {
      if(selectedPaymentMethod === ''){
        const defaultMethod = paymentMethods.find(method => method.isDefaultMethod);
        if (defaultMethod) {
          setSelectedPaymentMethod(defaultMethod.id);
          return;
        } else if (paymentMethods.length === 1) {
          // If only one is available, select it
          setSelectedPaymentMethod(paymentMethods[0].id);
          return;
        }
      }
    } else {
      setSelectedPaymentMethod('new');
    }

  }, [paymentMethods, selectedPaymentMethod]);

  return (
    <div className={styles.Container}>
      {/* Scrollable content */}
      <div className={styles.ScrollableContent}>
        {/* Price Info Display - TODO: Create a reusable component like PriceInfoDisplay */}
        <div className={styles.PriceInfoSection}>
          <h3 className={styles.ProductTitle}>
            {priceInfo.productName}
          </h3>
          <p>
            {priceInfo.productDescription}
          </p>
          <p>
            <strong>
              ${(priceInfo.unitAmount / 100).toFixed(2)} {priceInfo.currency.toUpperCase()}
              {priceInfo.recurringInterval && ` / ${priceInfo.recurringInterval}`}
            </strong>
          </p>
          
          {/* {taxesCalculation && (
            <div className={styles.TaxSection}>
              <Divider className={styles.TaxDivider} />
              <span><strong>{t('Tax')}:</strong> {taxesCalculation.taxDetails?.[0]?.type.toUpperCase() || '-'}</span>
              <br />
              <span><strong>{t('Rate')}:</strong> {taxesCalculation.taxDetails?.[0]?.percentage || '-'}</span>
              <br />
              <span><strong>{t('Reason')}:</strong> {taxesCalculation.taxDetails?.[0]?.reason || '-'}</span>
              <br />
              <span><strong>{t('Total')}:</strong> ${(taxesCalculation.amountTotal / 100).toFixed(2)} {taxesCalculation.currency.toUpperCase()}</span>
            </div>
          )} */}
        </div>

        {paymentMethods.length > 0 && (
          <div className={styles.PaymentMethodsSection}>
            <Typography.Title level={5}>{t('Title')}</Typography.Title>
            <div className={styles.PaymentMethodsList}>
              {paymentMethods.map((method) => (
                <PaymentMethodCard
                  key={method.id}
                  method={method}
                  isSelected={selectedPaymentMethod === method.id}
                  isLoading={isLoading}
                  isDeleting={deletingPaymentMethodId === method.id}
                  onSelect={setSelectedPaymentMethod}
                  onDelete={onDeletePaymentMethod}
                />
              ))}
              <div className={styles.AddNewMethodOption}>
                <Radio 
                  checked={selectedPaymentMethod === 'new'}
                  onChange={() => setSelectedPaymentMethod('new')}
                  disabled={isLoading || !!deletingPaymentMethodId}
                >
                  {t('AddNewPaymentMethod')}
                </Radio>
              </div>
            </div>
          </div>
        )}

        {(!paymentMethods.length || selectedPaymentMethod === 'new') && (
          <>
            <div className={styles.AddressSection}>
              <Typography.Text strong>{t('BillingAddress')}</Typography.Text>
              <div className={styles.AddressInputContainer}>
                <AddressElement
                  onChange={handleAddressChange}
                  options={{
                    mode: 'billing',
                    defaultValues: {
                      name: user.profile
                        ? `${user.profile.firstName || ''} ${user.profile.lastName || ''}`.trim()
                        : undefined,
                    }
                  }}
                />
              </div>
            </div>

            <div className={styles.PaymentSection}>
              <PaymentElement onChange={handlePaymentElementChange} />
            </div>
          </>
        )}
      </div>

      {checkoutError && (
        <div className={styles.CheckoutError}>
          <Alert message={checkoutError} type="error" closable onClose={() => setCheckoutError(null)} />
        </div>
      )}

      {/* Fixed footer with buttons */}
      <div className={styles.FixedFooter}>
        <form onSubmit={handleSubmit}>
          <Space className={styles.FooterButtonContainer}>
            <StyledButton
              title={t.withBaseKey('CommonWords')('Back')}
              color='light'
              noShadow
              disabled={isLoading || !!deletingPaymentMethodId}
              onClick={onClose || (() => dispatch(goToCheckoutStepAction('pricing')))}
            />
            <StyledButton
              title={t.withBaseKey('CommonWords')('Subscribe')}
              loading={isLoading}
              disabled={!isFormValid || !!deletingPaymentMethodId}
              type='submit'
            />
             
          </Space>
        </form>
      </div>
    </div>
  );
};

export default StripeCheckoutForm;
